import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "../../lib/utils";

interface ScrollXProps {
  children: ReactNode;
  /** Classes for the clipping frame (radius, ring, background). */
  className?: string;
}

/** Horizontal scroller for wide tables on narrow screens. When the content
 *  overflows it shows a right-edge fade while more columns remain and a
 *  one-line swipe hint, so a clipped table never reads as the whole table. */
export function ScrollX({ children, className }: ScrollXProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);
  const [moreRight, setMoreRight] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let hidden = 0;
    const onScroll = () => setMoreRight(hidden - el.scrollLeft > 4);
    const measure = () => {
      hidden = el.scrollWidth - el.clientWidth;
      setOverflows(hidden > 4);
      onScroll();
    };
    measure();
    el.addEventListener("scroll", onScroll, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);
    if (el.firstElementChild) observer?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener("scroll", onScroll);
      observer?.disconnect();
    };
  }, []);

  return (
    <div className={cn("overflow-hidden", className)}>
      <div className="relative">
        <div
          ref={scrollerRef}
          className="overflow-x-auto overscroll-x-contain focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-purple-500"
          {...(overflows
            ? { tabIndex: 0, role: "region", "aria-label": "Table, scrolls sideways" }
            : {})}
        >
          {children}
        </div>
        <div
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute top-0 right-0 bottom-0 w-12 bg-gradient-to-l from-white to-white/0 transition-opacity duration-200 motion-reduce:transition-none",
            moreRight ? "opacity-100" : "opacity-0",
          )}
        />
      </div>
      {overflows ? (
        <p
          aria-hidden="true"
          className="flex items-center gap-1.5 border-t border-neutral-100 px-4 py-2 text-xs font-medium text-neutral-500"
        >
          Scroll sideways for more columns
          <i className="fa-regular fa-arrow-right text-[10px]" />
        </p>
      ) : null}
    </div>
  );
}
